import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Feather, LogOut, Menu, PenLine, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const Navbar = () => {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground transition-transform duration-500 group-hover:rotate-12">
            <Feather className="h-4 w-4" />
          </span>
          <span className="font-serif text-lg font-semibold tracking-tight">
            CK Finding Fauna
          </span>
        </Link>

        <nav className="flex items-center gap-2 md:gap-6">
          <Link to="/about" data-testid="nav-about-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
            About
          </Link>
          <Link to="/get-involved" data-testid="nav-get-involved-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
            Get Involved
          </Link>
          <Link to="/clubs" data-testid="nav-clubs-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
            Clubs
          </Link>
          <Link to="/education" data-testid="nav-education-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
            Education
          </Link>
          <Link to="/qa" data-testid="nav-qa-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
            Q&amp;A
          </Link>
          {onHome && (
            <>
              <a href="#gallery" data-testid="nav-gallery-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
                Gallery
              </a>
              <a href="#map" data-testid="nav-map-link" className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block">
                Map
              </a>
            </>
          )}
          {status === "authed" && user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin/users"
                  data-testid="nav-users-link"
                  className="hidden text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground md:block"
                >
                  Users
                </Link>
              )}
              <Link
                to="/new"
                data-testid="nav-new-entry-button"
                className="flex items-center gap-2 bg-primary px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
              >
                <PenLine className="h-3.5 w-3.5" />
                New Entry
              </Link>
              <span data-testid="nav-user-name" className="hidden items-center gap-2 text-sm text-muted-foreground md:flex">
                {user.name}
                {user.role === "admin" && (
                  <span data-testid="nav-admin-badge" className="bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground">
                    Admin
                  </span>
                )}
              </span>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-button"
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-300 hover:text-foreground"
              >
                <LogOut className="h-3.5 w-3.5" />
                Out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-sign-in-button"
              className="border border-foreground px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-foreground hover:text-background"
            >
              Sign In
            </Link>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
            className="flex h-9 w-9 items-center justify-center border border-foreground transition-colors duration-300 hover:bg-foreground hover:text-background md:hidden"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </nav>
      </div>

      {menuOpen && (
        <motion.nav
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="border-b border-border bg-background/95 backdrop-blur-xl md:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col px-6 py-4">
            {[
              { to: "/about", label: "About", testId: "mobile-about-link" },
              { to: "/get-involved", label: "Get Involved", testId: "mobile-get-involved-link" },
              { to: "/clubs", label: "Clubs", testId: "mobile-clubs-link" },
              { to: "/education", label: "Education", testId: "mobile-education-link" },
              { to: "/qa", label: "Q&A", testId: "mobile-qa-link" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className="border-b border-border/60 py-4 font-serif text-xl font-semibold tracking-tight"
              >
                {l.label}
              </Link>
            ))}
            {onHome && (
              <>
                <a href="#gallery" data-testid="mobile-gallery-link" className="border-b border-border/60 py-4 font-serif text-xl font-semibold tracking-tight">
                  Gallery
                </a>
                <a href="#map" data-testid="mobile-map-link" className="border-b border-border/60 py-4 font-serif text-xl font-semibold tracking-tight">
                  Map
                </a>
              </>
            )}
            {status === "authed" ? (
              <Link to="/new" data-testid="mobile-new-entry-link" className="py-4 font-serif text-xl font-semibold tracking-tight text-primary">
                New Entry
              </Link>
            ) : (
              <Link to="/login" data-testid="mobile-sign-in-link" className="py-4 font-serif text-xl font-semibold tracking-tight text-primary">
                Sign In
              </Link>
            )}
          </div>
        </motion.nav>
      )}
    </header>
  );
};
